import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card, Button, Spinner, Row, Col, Form, Alert, Dropdown
} from "react-bootstrap";
import DataApi from "../../api/dataApi";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ResizableTable from "../common/ResizableTable";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../App.css";

const CentralAcademyReport = () => {
  const navigate = useNavigate();

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // -- Filter States --
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    days: "",
    category: "",
    status: "",
    is_book_bank: ""
  });

  // Custom Date States
  const [customDates, setCustomDates] = useState({ start: "", end: "" });

  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);

  // -- Dynamic Dropdown Options --
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const labelStyle = {
    fontSize: '0.875rem', fontWeight: '600', color: '#495057',
    marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'
  };
  const inputBaseStyle = {
    height: '44px', borderRadius: '8px', border: '1.5px solid #dee2e6',
    padding: '0.625rem 1rem', fontSize: '0.9375rem', transition: 'all 0.2s ease'
  };

  const columns = [
    { field: "Sr.No", label: "Sr No", align: "center", width: 80 },
    { field: "Accession No", label: "Accession No", searchable: true, align: "center" },
    { field: "Book Name", label: "Book Name", searchable: true, align: "left" },
    { field: "Accession Date", label: "Accession Date", align: "center", render: (value) => value ? value.split('T')[0] : '-' },
    { field: "ISBN NO", label: "ISBN", align: "center" },
    { field: "Item Status", label: "Status", align: "center" },
    { field: "Book Category", label: "Category", searchable: true, align: "center" },
    { field: "Language", label: "Language", align: "center" },
    { field: "Classification No", label: "Class No", align: "center" },
    { field: "Volume", label: "Volume", align: "center" },
    { field: "Edition", label: "Edition", align: "center" },
    { field: "No Of Pages", label: "Pages", align: "center" },
    { field: "Author", label: "Author", searchable: true, align: "left" },
    { field: "Publisher", label: "Publisher", searchable: true, align: "center" },
    { field: "Vendor Name", label: "Vendor", align: "center" },
    { field: "Year Of Publication", label: "Year", align: "center" },
    { field: "Price", label: "Price", align: "right" },
    { field: "Quantity", label: "Quantity", align: "right" },
    { field: "Rack No", label: "Rack", align: "center" },
    { field: "Shelf No", label: "Shelf", align: "center" },
    { field: "Bill No", label: "Bill No", align: "center" },
    { field: "Bill Date", label: "Bill Date", align: "center", render: (value) => value ? value.split('T')[0] : '-' },
    { field: "Subject", label: "Subject", align: "center" }
  ];

  // Wrapped in useCallback to safely use in useEffect
  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const api = new DataApi("reports/central-academy");
      const params = {};

      if (filters.category) params.category = filters.category.trim();
      if (filters.status) params.status = filters.status.trim();

      if (filters.is_book_bank !== "") {
        params.is_book_bank = filters.is_book_bank;
      }

      const getPastDate = (daysAgo) => {
        const d = new Date();
        d.setDate(d.getDate() - daysAgo);
        return d.toISOString().split('T')[0];
      };
      const todayStr = new Date().toISOString().split('T')[0];

      if (filters.days === "30") {
        params.startDate = getPastDate(30);
        params.endDate = todayStr;
      } else if (filters.days === "90") {
        params.startDate = getPastDate(90);
        params.endDate = todayStr;
      } else if (filters.days === "365") {
        params.startDate = getPastDate(365);
        params.endDate = todayStr;
      } else if (filters.days === "custom" && customDates.start && customDates.end) {
        params.startDate = customDates.start;
        params.endDate = customDates.end;
      }

      const response = await api.get('', { params });

      // FIXED: Fallback to response.data directly in case the backend returns an array instead of { records: [...] }
      const rawData = response?.data?.records || response?.data?.data || response?.data || [];

      const dataWithIds = rawData.map((item, index) => ({
        ...item,
        id: item["Accession No"] || `row-${index}`,
        Price: parseFloat(item["Price"]) || 0,
        Quantity: 1
      }));

      setReportData(dataWithIds);

      // Only extract options if they haven't been populated yet
      if (categories.length === 0 || statuses.length === 0) {
        extractFilterOptions(dataWithIds);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      // FIXED: Reveal the actual backend error on the UI
      const serverError = err.response?.data?.message || err.response?.data?.error || err.message || "Unknown error occurred.";
      setError(`Failed to load Report: ${serverError}`);
    } finally {
      setLoading(false);
    }
  }, [filters, customDates, categories.length, statuses.length]);

  const extractFilterOptions = (data) => {
    const cats = [...new Set(data.map(item => item["Book Category"]).filter(Boolean))];
    setCategories(cats);

    const stats = [...new Set(data.map(item => item["Item Status"]).filter(Boolean))];
    setStatuses(stats);
  };

  useEffect(() => {
    if (filters.days !== "custom" || (filters.days === "custom" && customDates.start && customDates.end)) {
      fetchReport();
    }
  }, [fetchReport]);

  const filteredData = useMemo(() => {
    if (!searchTerm || !searchTerm.trim()) return reportData;

    const searchTerms = searchTerm.toLowerCase().trim().split(/\s+/);

    return reportData.filter((row) => {
      const rowSearchableString = columns
        .filter(col => col.searchable)
        .map(col => String(row[col.field] || "").toLowerCase())
        .join(" ");

      return searchTerms.every(term => rowSearchableString.includes(term));
    });
  }, [reportData, searchTerm]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({ days: "", category: "", status: "", is_book_bank: "" });
    setSearchTerm("");
    setCustomDates({ start: "", end: "" });
    setCurrentPage(1);
  };

  // --- Export Functions ---
  const getExportData = () => {
    const dataToExport = selectedRows.length > 0 ? filteredData.filter(row => selectedRows.includes(row.id)) : filteredData;
    if (dataToExport.length === 0) {
      toast.error("No data to export!");
      return null;
    }
    return dataToExport;
  };

  const exportExcel = () => {
    const dataToExport = getExportData();
    if (!dataToExport) return;
    const exportData = dataToExport.map(row => {
      const rowData = {};
      columns.forEach(col => rowData[col.label] = row[col.field] || "N/A");
      return rowData;
    });
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Central Academy Report");
    XLSX.writeFile(workbook, `Central_Academy_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportCSV = () => {
    const dataToExport = getExportData();
    if (!dataToExport) return;
    const exportData = dataToExport.map(row => {
      const rowData = {};
      columns.forEach(col => rowData[col.label] = row[col.field] || "N/A");
      return rowData;
    });
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `Central_Academy_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportPDF = () => {
    const dataToExport = getExportData();
    if (!dataToExport) return;
    const headers = columns.map(col => col.label);
    const exportData = dataToExport.map(row => columns.map(col => row[col.field] || "N/A"));
    const doc = new jsPDF("landscape");
    doc.setFontSize(16);
    doc.text("Central Academy Report", 14, 20);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()} | Records: ${dataToExport.length}`, 14, 30);
    autoTable(doc, {
      head: [headers],
      body: exportData,
      startY: 40,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: 14, right: 14 }
    });
    doc.save(`Central_Academy_Report_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
        <div className="mt-3 text-muted">Loading Central Academy Report...</div>
      </div>
    );
  }

  const isCustom = filters.days === "custom";

  return (
    <>
      <div className="container-fluid" style={{ marginTop: "-14px" }}>
        <Card className="border-0 shadow-sm bg-transparent">

          <div className="library-header border shadow-sm mt-3 mb-4 rounded mx-2 d-flex justify-content-between align-items-center" style={{ padding: '12px 16px', backgroundColor: '#fff' }}>
            <div className="d-flex align-items-center gap-3 ms-2">
              <button
                onClick={() => navigate('/reports')}
                className="shadow-sm d-flex align-items-center justify-content-center custom-btn-back"
              >
                <i className="fa-solid fa-arrow-left"></i>
              </button>
              <div>
                <h4 className="mb-0 fw-bold fs-5" style={{ color: "var(--primary-color)" }}>
                  <i className="fa-solid fa-file-lines me-2 mb-1" /> Central Academy Report
                </h4>
                <small className="text-muted">Book Accession & Inventory Master List</small>
              </div>
            </div>

            <div className="library-header-right me-2">
              <Dropdown align="end">
                <Dropdown.Toggle variant="light" size="sm" className="border shadow-sm">
                  <i className="fa fa-download me-1" /> Export
                </Dropdown.Toggle>
                <Dropdown.Menu className="shadow-sm border-0 mt-2">
                  <Dropdown.Item onClick={exportExcel}>
                    <i className="fa-solid fa-file-excel me-2 text-success" /> Excel
                  </Dropdown.Item>
                  <Dropdown.Item onClick={exportCSV}>
                    <i className="fa-solid fa-file-csv me-2 text-info" /> CSV
                  </Dropdown.Item>
                  <Dropdown.Item onClick={exportPDF}>
                    <i className="fa-solid fa-file-pdf me-2 text-danger" /> PDF
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          <Card.Body className="pt-0">
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                <i className="fa fa-exclamation-circle me-2" />
                {error}
              </Alert>
            )}

            <Card className="border-0 mb-4 shadow-sm mx-2">
              <Card.Body>
                <Row className="g-3 align-items-end">
                  <Col md={2}>
                    <div style={labelStyle}>
                      <i className="fa-solid fa-calendar-days"></i>
                      <span>Period</span>
                    </div>
                    <Form.Select
                      value={filters.days}
                      onChange={(e) => handleFilterChange('days', e.target.value)}
                      style={inputBaseStyle}
                    >
                      <option value="">All Time</option>
                      <option value="30">Last 30 Days</option>
                      <option value="90">Last 90 Days</option>
                      <option value="365">Last Year</option>
                      <option value="custom">Custom Range</option>
                    </Form.Select>
                  </Col>

                  {isCustom && (
                    <>
                      <Col md={2}>
                        <div style={labelStyle}><span>Start Date</span></div>
                        <Form.Control
                          type="date"
                          value={customDates.start}
                          onChange={(e) => setCustomDates(prev => ({ ...prev, start: e.target.value }))}
                          style={inputBaseStyle}
                        />
                      </Col>
                      <Col md={2}>
                        <div style={labelStyle}><span>End Date</span></div>
                        <Form.Control
                          type="date"
                          value={customDates.end}
                          onChange={(e) => setCustomDates(prev => ({ ...prev, end: e.target.value }))}
                          style={inputBaseStyle}
                        />
                      </Col>
                    </>
                  )}

                  <Col md={2}>
                    <div style={labelStyle}>
                      <i className="fa-solid fa-circle-info"></i>
                      <span>Status</span>
                    </div>
                    <Form.Select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      style={inputBaseStyle}
                    >
                      <option value="">All Statuses</option>
                      {statuses.map(stat => <option key={stat} value={stat}>{stat}</option>)}
                    </Form.Select>
                  </Col>

                  <Col md={2}>
                    <div style={labelStyle}>
                      <i className="fa-solid fa-book-bookmark"></i>
                      <span>Book Type</span>
                    </div>
                    <Form.Select
                      value={filters.is_book_bank}
                      onChange={(e) => handleFilterChange('is_book_bank', e.target.value)}
                      style={inputBaseStyle}
                    >
                      <option value="">All Books</option>
                      <option value="true">Book Bank Only</option>
                      <option value="false">Regular Books Only</option>
                    </Form.Select>
                  </Col>

                  <Col md={isCustom ? 2 : 2}>
                    <div style={labelStyle}>
                      <i className="fa-solid fa-tag"></i>
                      <span>Category</span>
                    </div>
                    <Form.Select
                      value={filters.category}
                      onChange={(e) => handleFilterChange('category', e.target.value)}
                      style={inputBaseStyle}
                    >
                      <option value="">All Categories</option>
                      {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </Form.Select>
                  </Col>

                  <Col md={isCustom ? 2 : 3}>
                    <div style={labelStyle}>
                      <i className="fa-solid fa-magnifying-glass"></i>
                      <span>Quick Search</span>
                    </div>
                    <Form.Control
                      type="text"
                      placeholder="Title, Author..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={inputBaseStyle}
                    />
                  </Col>

                  <Col xs="auto" className={isCustom ? "mt-3 text-end" : "px-1"}>
                    <Button variant="light" onClick={resetFilters} className="border shadow-sm d-flex justify-content-center align-items-center" style={{ height: '44px' }}>
                      <i className="fa-solid fa-undo text-secondary" />
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <div className="table-responsive mx-2">
              <ResizableTable
                data={filteredData}
                columns={columns}
                loading={loading}
                currentPage={currentPage}
                recordsPerPage={rowsPerPage}
                onPageChange={setCurrentPage}
                showSerialNumber={false}
                showCheckbox={true}
                showActions={false}
                selectedItems={selectedRows}
                onSelectionChange={setSelectedRows}
              />
            </div>
          </Card.Body>
        </Card>
      </div>
    </>
  );
};

export default CentralAcademyReport;